import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, XCircle, ChevronDown, XIcon, PlusIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva('m-1 rounded-lg transition-colors duration-150', {
  variants: {
    variant: {
      default: 'bg-accent text-accent-foreground border border-transparent hover:bg-accent/80',
      secondary:
        'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
      destructive:
        'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      inverted: 'inverted',
      fixed: 'border-dashed bg-gray-300 text-gray-600 cursor-not-allowed pointer-events-none'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    label: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * An array of fixed option objects that are always selected and cannot be removed.
   */
  fixedValues?: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /** The selected values when the component mounts. */
  value?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Determines whether users can add custom values.
   * - `true`: Users can add custom values.
   * - `false`: Users cannot add custom values.
   * @default true
   */
  allowCustomAdditions?: boolean;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Optional callback for handling custom actions (e.g., "Create new Segment")
   * This replaces the custom additions functionality when provided
   */
  onCustomAction?: () => void;

  /**
   * Label for the custom action button
   * Only used when onCustomAction is provided
   */
  customActionLabel?: string;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value,
      fixedValues = [],
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = 'Select options',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      className,
      allowCustomAdditions = true,
      onCustomAction,
      customActionLabel = 'Add Custom',
      ...props
    }: MultiSelectProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    // Dedupe values, giving priority to fixedValues
    const dedupeValues = React.useCallback(
      (values: string[]) => {
        const fixedSet = new Set(fixedValues.map((fv) => fv.value));
        return [...new Set([...fixedSet, ...values.filter((v) => !fixedSet.has(v))])];
      },
      [fixedValues]
    );

    // State to manage user-selected values (deduped)
    const [userSelectedValues, setUserSelectedValues] = React.useState<string[]>(
      dedupeValues(value ?? defaultValue).filter((v) => !fixedValues.some((fv) => fv.value === v))
    );

    // Update internal state when controlled value changes
    React.useEffect(() => {
      const newSelected = dedupeValues(value ?? defaultValue).filter(
        (v) => !fixedValues.some((fv) => fv.value === v)
      );
      setUserSelectedValues((prevSelected) => {
        if (
          prevSelected.length === newSelected.length &&
          prevSelected.every((val, idx) => val === newSelected[idx])
        ) {
          return prevSelected;
        }
        return newSelected;
      });
    }, [value, defaultValue, fixedValues, dedupeValues]);

    // Combined selected values including fixed values (already deduped)
    const combinedSelectedValues = React.useMemo(
      () => dedupeValues([...fixedValues.map((fv) => fv.value), ...userSelectedValues]),
      [fixedValues, userSelectedValues, dedupeValues]
    );

    // State to manage popover visibility
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    // State to manage animation
    const [isAnimating, _setIsAnimating] = React.useState(false);

    // State to manage dynamic options (for custom inputs)
    const [optionsState, setOptionsState] = React.useState(options);

    // Callers derive `options` from the form value, which is populated
    // asynchronously, so the prop changes after mount. Merge it in rather than
    // replacing, so custom additions that haven't round-tripped through the
    // parent yet survive.
    React.useEffect(() => {
      setOptionsState((prevOptions) => {
        const fromProps = new Set(options.map((o) => o.value));
        const merged = [...options, ...prevOptions.filter((o) => !fromProps.has(o.value))];
        const unchanged =
          merged.length === prevOptions.length &&
          merged.every(
            (option, idx) =>
              option.value === prevOptions[idx].value && option.label === prevOptions[idx].label
          );
        return unchanged ? prevOptions : merged;
      });
    }, [options]);

    // State to manage input value
    const [inputValue, setInputValue] = React.useState<string>('');

    // Handler for toggling options
    const toggleOption = (option: string) => {
      // Prevent toggling fixed values
      if (fixedValues.some((fv) => fv.value === option)) return;

      let newUserSelectedValues: string[];
      if (userSelectedValues.includes(option)) {
        newUserSelectedValues = userSelectedValues.filter((value) => value !== option);
      } else {
        newUserSelectedValues = [...userSelectedValues, option];
      }
      setUserSelectedValues(newUserSelectedValues);
      onValueChange(dedupeValues([...fixedValues.map((fv) => fv.value), ...newUserSelectedValues]));
    };

    // Handler to clear only user-selected values
    const handleClear = () => {
      setUserSelectedValues([]);
      onValueChange([...fixedValues.map((fv) => fv.value)]);
    };

    // Handler to clear extra options beyond maxCount
    const clearExtraOptions = () => {
      setUserSelectedValues([]);
      onValueChange([...fixedValues.map((fv) => fv.value)]);
    };

    // Handler to toggle all user-selectable options
    const toggleAll = () => {
      const allUserOptions = optionsState
        .map((option) => option.value)
        .filter((val) => !fixedValues.some((fv) => fv.value === val));
      if (userSelectedValues.length === allUserOptions.length) {
        handleClear();
      } else {
        setUserSelectedValues(allUserOptions);
        onValueChange([...fixedValues.map((fv) => fv.value), ...allUserOptions]);
      }
    };

    // Determine if the custom add option should be shown
    const showAddOption =
      allowCustomAdditions &&
      !optionsState.some(
        (o) =>
          o.label.toLowerCase() === inputValue.trim().toLowerCase() ||
          o.value.toLowerCase() === inputValue.trim().toLowerCase()
      ) &&
      !fixedValues.some(
        (fv) =>
          fv.label.toLowerCase() === inputValue.trim().toLowerCase() ||
          fv.value.toLowerCase() === inputValue.trim().toLowerCase()
      );

    // Handler for adding a custom option
    const addCustomOption = (value: string) => {
      const newOption = { label: value, value };
      setOptionsState((prev) => [...prev, newOption]);
      setUserSelectedValues((prev) => [...prev, value]);
      onValueChange([...fixedValues.map((fv) => fv.value), ...userSelectedValues, value]);
      setInputValue(''); // Clear the input after adding
    };

    // Handler for input key down events (for custom input)
    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (showAddOption) {
          addCustomOption(inputValue.trim());
          event.preventDefault();
        } else {
          // Optionally, you can toggle the popover or perform another action
          setIsPopoverOpen(true);
        }
      } else if (event.key === 'Backspace' && !inputValue) {
        if (userSelectedValues.length > 0) {
          const newUserSelectedValues = [...userSelectedValues];
          newUserSelectedValues.pop();
          setUserSelectedValues(newUserSelectedValues);
          onValueChange([...fixedValues.map((fv) => fv.value), ...newUserSelectedValues]);
        }
      }
    };

    // Determine if we should show the custom action button
    const showCustomAction = !allowCustomAdditions && onCustomAction && customActionLabel;

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={() => {
              setIsPopoverOpen((prev) => !prev);
            }}
            className={cn(
              `flex w-full p-1 rounded-lg border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit border-[hsl(var(--border))] bg-[hsl(var(--input))]`,
              className
            )}
          >
            {combinedSelectedValues.length > 0 ? (
              <div className="flex justify-between items-center w-full">
                <div className="flex flex-wrap items-center">
                  {/* Render Fixed Values */}
                  {fixedValues.map((fixed) => {
                    const IconComponent = fixed.icon;
                    return (
                      <Badge
                        key={fixed.value}
                        className={cn(
                          'mr-1 mb-1',
                          multiSelectVariants({ variant: 'fixed' }) // Use fixed variant
                        )}
                        title="Fixed value"
                      >
                        {IconComponent && <IconComponent className="h-4 w-4 mr-1" />}
                        {fixed.label}
                      </Badge>
                    );
                  })}

                  {/* Render User-Selected Values */}
                  {userSelectedValues.slice(0, maxCount).map((value) => {
                    const option = optionsState.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge
                        variant="secondary"
                        key={value}
                        className={cn(
                          isAnimating ? 'animate-bounce' : '',
                          multiSelectVariants({ variant }),
                          'rounded-lg text-xs font-normal'
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                        {option?.label ?? value}
                        <XCircle
                          className="ml-2 h-4 w-4 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}

                  {/* Handle Extra Selected Values */}
                  {userSelectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-transparent text-foreground border-foreground/1 hover:bg-transparent text-xs',
                        isAnimating ? 'animate-bounce' : '',
                        multiSelectVariants({ variant })
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${userSelectedValues.length - maxCount} more`}
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <XIcon
                    className="h-4 mx-2 cursor-pointer text-muted-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator orientation="vertical" className="flex min-h-6 h-full" />
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">{placeholder}</span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-1 rounded-lg border-[hsl(var(--border))]"
          align="start"
          onEscapeKeyDown={() => {
            setIsPopoverOpen(false);
          }}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              value={inputValue}
              onValueChange={(value) => {
                setInputValue(value);
              }}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {optionsState.length > 0 && (
                  <CommandItem key="all" onSelect={toggleAll} className="cursor-pointer">
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        userSelectedValues.length === optionsState.length
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>(Select All)</span>
                  </CommandItem>
                )}
                {optionsState.map((option) => {
                  const isSelected = combinedSelectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => {
                        toggleOption(option.value);
                      }}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {option.icon && (
                        <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}

                {/* Show either custom action button or custom additions UI */}
                {showCustomAction ? (
                  <CommandItem
                    key="custom-action"
                    onSelect={() => {
                      onCustomAction();
                      setIsPopoverOpen(false);
                    }}
                    className="cursor-pointer text-primary hover:bg-primary/10"
                  >
                    <PlusIcon className="mr-2 h-4 w-4 text-primary" />
                    {customActionLabel}
                  </CommandItem>
                ) : (
                  showAddOption && (
                    <CommandItem
                      key="add-custom"
                      onSelect={() => {
                        if (inputValue) {
                          addCustomOption(inputValue);
                        }
                      }}
                      className="cursor-pointer text-primary hover:bg-primary/10"
                    >
                      <PlusIcon className="mr-2 h-4 w-4 text-primary" />
                      {inputValue
                        ? `Add Custom Filter: "${inputValue}"`
                        : 'Start typing to add a custom filter...'}
                    </CommandItem>
                  )
                )}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {combinedSelectedValues.length > fixedValues.length && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer"
                      >
                        Clear
                      </CommandItem>
                      <Separator orientation="vertical" className="flex min-h-6 h-full" />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => {
                      setIsPopoverOpen(false);
                    }}
                    className="flex-1 justify-center cursor-pointer max-w-full"
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
